import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { witherwildContent } from '../content/witherwild';
import { useStrings } from '../locales';
import './WitherwildFrame.css';

const ALL_TABS = Object.keys(witherwildContent);

/* Leaves along one half of the vine: x, y, angle */
const LEAVES: Array<[number, number, number]> = [[26, 10, -40], [52, 9, 30], [80, 13, -35], [108, 17, 35], [134, 15, -30]];

/** A vine with leaves on both sides of a five-petal blossom (the crimson lady's veil). */
function VineDivider() {
  const half = (
    <>
      <path d="M8 14 C60 4 100 24 148 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      {LEAVES.map(([x, y, a]) => (
        <path key={x} d="M0 0 C3 -4.5 9 -4.5 12 0 C9 4.5 3 4.5 0 0Z" transform={`translate(${x} ${y}) rotate(${a})`} fill="currentColor" />
      ))}
    </>
  );
  return (
    <svg className="ww-vine" viewBox="0 0 320 28" aria-hidden="true">
      {half}
      <g transform="translate(320 0) scale(-1 1)">{half}</g>
      <g transform="translate(160 14)">
        {[0, 72, 144, 216, 288].map((a) => <ellipse key={a} cx="0" cy="-5.5" rx="2.8" ry="5" transform={`rotate(${a})`} fill="currentColor" />)}
        <circle r="2.4" className="ww-vine__heart" />
      </g>
    </svg>
  );
}

export default function WitherwildFrame() {
  const s = useStrings().campaignFrame;
  const LABELS: Record<string, string> = {
    'OVERVIEW': s.tabs.overview,
    'COMMUNITIES': s.tabs.communities,
    'CLASSES & ANCESTRIES': s.tabs.classesAncestries,
    'PRINCIPLES': s.tabs.principles,
    'DISTINCTIONS': s.tabs.distinctions,
    'CAMPAIGN': s.tabs.campaign,
  };
  const label = (key: string) => LABELS[key] ?? key.charAt(0) + key.slice(1).toLowerCase();

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(() => {
    const raw = (searchParams.get('tab') ?? '').toUpperCase();
    return ALL_TABS.includes(raw) ? raw : ALL_TABS[0];
  });

  const currentIdx = ALL_TABS.indexOf(activeTab);
  const prevTab = currentIdx > 0 ? ALL_TABS[currentIdx - 1] : null;
  const nextTab = currentIdx < ALL_TABS.length - 1 ? ALL_TABS[currentIdx + 1] : null;

  const goTo = (key: string) => {
    setActiveTab(key);
    document.querySelector('.content-area')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const Content = witherwildContent[activeTab];

  return (
    <div className="witherwild-theme">
      <div className="page-content">
        {/* Hero */}
        <div className="ww-hero">
          <h1>{s.title}</h1>
          <VineDivider />
          <p className="ww-hero__tagline"><em>When an invading nation attacks an ancient forest deity, unchecked growth corrupts the land and its people must find a way to restore balance.</em></p>
          <p className="ww-hero__credits">{s.desc}</p>
          <div className="ww-complexity" role="img" aria-label="Complexity: 1 of 4">
            <span className="ww-complexity__label" aria-hidden="true">Complexity:</span>
            <span className="ww-complexity-dot ww-complexity-dot--filled" />
            <span className="ww-complexity-dot" />
            <span className="ww-complexity-dot" />
            <span className="ww-complexity-dot" />
          </div>
        </div>

        <div className="mechanics-layout">
          {/* Sidebar TOC */}
          <nav className="mechanics-toc">
            <h4>{s.contents}</h4>
            {ALL_TABS.map((key) => (
              <button
                key={key}
                className={`toc-link${activeTab === key ? ' active' : ''}`}
                onClick={() => goTo(key)}
              >
                {label(key)}
              </button>
            ))}
          </nav>

          <div className="mechanics-content">
            {/* Mobile dropdown */}
            <div className="mechanics-mobile-nav">
              <select
                className="mechanics-select"
                value={activeTab}
                onChange={(e) => goTo(e.target.value)}
                aria-label="Select section"
              >
                {ALL_TABS.map((key) => (
                  <option key={key} value={key}>{label(key)}</option>
                ))}
              </select>
              <span className="mechanics-select-count">
                {currentIdx + 1} / {ALL_TABS.length}
              </span>
            </div>

            {/* Content */}
            {Content && (
              <div className="rule-section">
                <h2>{label(activeTab)}</h2>
                <Content />
              </div>
            )}

            {/* Prev / Next */}
            <div className="mechanics-section-nav">
              {prevTab
                ? <button className="mechanics-nav-btn mechanics-nav-btn--prev" onClick={() => goTo(prevTab)}>
                  <ChevronLeft size={20} className="mechanics-nav-btn__arrow" />
                  <span className="mechanics-nav-btn__label">{label(prevTab)}</span>
                </button>
                : <span />
              }
              {nextTab
                ? <button className="mechanics-nav-btn mechanics-nav-btn--next" onClick={() => goTo(nextTab)}>
                  <span className="mechanics-nav-btn__label">{label(nextTab)}</span>
                  <ChevronRight size={20} className="mechanics-nav-btn__arrow" />
                </button>
                : <span />
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
